import React from 'react';
import { Box, Search, X, Plus, Trash2 } from 'lucide-react';
import RightDrawer from '../../../shared/components/form/RightDrawer';

interface MasterAssetSelectionDrawerProps {
    open: boolean;
    onClose: () => void;
    availableAssets: Array<{
        masterAssetId: string;
        name: string;
        description?: string;
    }>;
    existingProjectAssets: any[];
    onSelectAsset: (asset: any) => void;
    onBulkAdd?: (assets: any[]) => void;
    onRemoveAsset?: (projectAsset: any) => void;
    /** When provided, shows a "Create New Asset" action in the empty state (no master assets found for this project type). */
    onCreateAsset?: () => void;
}

export default function MasterAssetSelectionDrawer({
    open,
    onClose,
    availableAssets,
    existingProjectAssets,
    onSelectAsset,
    onBulkAdd,
    onRemoveAsset,
    onCreateAsset
}: MasterAssetSelectionDrawerProps) {
    const [searchQuery, setSearchQuery] = React.useState('');
    const [selectedAssetIds, setSelectedAssetIds] = React.useState<Set<string>>(new Set());

    // Check which assets are already added to project
    const getAssetStatus = (asset: any) => {
        const exists = existingProjectAssets.some(pa => {
            const projectAssetMasterId = typeof pa.masterAssetId === 'string'
                ? pa.masterAssetId
                : pa.masterAssetId?._id || pa.masterAssetId?.id;
            return projectAssetMasterId === asset.masterAssetId;
        });
        return exists;
    };

    // Filter assets based on search only - show both available and already-added
    const filteredAssets = availableAssets.filter(asset => {
        // Search filter
        if (searchQuery) {
            return asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (asset.description && asset.description.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return true;
    });

    // Separate into available and already-added
    const availableAssetsFiltered = filteredAssets.filter(asset => !getAssetStatus(asset));
    const alreadyAddedAssets = filteredAssets.filter(asset => getAssetStatus(asset));

    const toggleSelection = (id: string, isAlreadyAdded: boolean) => {
        if (isAlreadyAdded) return; // Cannot select already-added assets

        const newSet = new Set(selectedAssetIds);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedAssetIds(newSet);
    };

    const toggleAll = () => {
        if (selectedAssetIds.size === availableAssetsFiltered.length) {
            setSelectedAssetIds(new Set());
        } else {
            const allIds = new Set(availableAssetsFiltered.map(a => a.masterAssetId));
            setSelectedAssetIds(allIds);
        }
    };

    const handleBulkAddClick = () => {
        if (!onBulkAdd) return;
        const selectedAssets = availableAssets.filter(a => selectedAssetIds.has(a.masterAssetId));
        onBulkAdd(selectedAssets);
        onClose();
        setSelectedAssetIds(new Set());
    };

    return (
        <RightDrawer
            open={open}
            onClose={onClose}
            title="Add Project Asset"
            size="md"
        >
            <div className="flex flex-col h-full bg-gray-50">
                {/* Search Header */}
                <div className="p-4 bg-white border-b sticky top-0 z-10 space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search master assets..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#339274] focus:border-transparent outline-none"
                        />
                    </div>

                    {/* Info Banner - Show stats about available vs already added */}
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                        <div className="flex items-center gap-1 text-blue-700">
                            <span className="font-semibold">{availableAssetsFiltered.length}</span>
                            <span>available</span>
                        </div>
                        <span className="text-blue-400">•</span>
                        <div className="flex items-center gap-1 text-gray-600">
                            <span className="font-semibold">{alreadyAddedAssets.length}</span>
                            <span>already added</span>
                        </div>
                    </div>

                    {onBulkAdd && availableAssetsFiltered.length > 0 && (
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-500">
                                {selectedAssetIds.size} selected
                            </div>
                            <button
                                onClick={toggleAll}
                                className="text-sm font-medium text-[#339274] hover:underline"
                            >
                                {selectedAssetIds.size === availableAssetsFiltered.length ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Assets List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {filteredAssets.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <Box size={40} className="mb-3 opacity-20 text-gray-400" />
                            {searchQuery ? (
                                <>
                                    <p className="text-gray-600 font-medium">No assets match your search</p>
                                    <p className="text-sm text-gray-500 mt-1">Try a different search term</p>
                                </>
                            ) : (
                                <>
                                    <p className="text-gray-600 font-medium">No master assets available for this project type</p>
                                    <p className="text-sm text-gray-500 mt-1">Create a master asset to add it to this project</p>
                                    {onCreateAsset && (
                                        <button
                                            onClick={onCreateAsset}
                                            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#339274] text-white rounded-lg text-sm font-medium hover:bg-[#29795f] transition-colors shadow-sm"
                                        >
                                            <Plus size={16} />
                                            Create New Asset
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Available Assets (White background, selectable) */}
                            {availableAssetsFiltered.map(asset => {
                                const isSelected = selectedAssetIds.has(asset.masterAssetId);
                                return (
                                    <div
                                        key={asset.masterAssetId}
                                        className={`bg-white rounded-lg border p-4 transition-all group cursor-pointer ${isSelected
                                            ? 'border-[#339274] ring-1 ring-[#339274] bg-green-50/10'
                                            : 'border-gray-200 hover:border-[#339274] hover:shadow-md'
                                            }`}
                                        onClick={() => toggleSelection(asset.masterAssetId, false)}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-3">
                                                <div className="pt-0.5">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => { }} // Handled by div click
                                                        className="w-4 h-4 rounded text-[#339274] focus:ring-[#339274] border-gray-300"
                                                    />
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-semibold text-gray-900">{asset.name}</h4>
                                                    {asset.description && (
                                                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{asset.description}</p>
                                                    )}
                                                </div>
                                            </div>

                                            {!onBulkAdd && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onSelectAsset(asset);
                                                        onClose();
                                                    }}
                                                    className="px-3 py-1.5 bg-white text-[#339274] border border-[#339274] rounded-md text-xs font-medium hover:bg-[#339274] hover:text-white transition-colors shadow-sm flex items-center gap-1 flex-shrink-0"
                                                >
                                                    <Plus size={14} />
                                                    Add
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Already Added Assets (Gray background, with remove option) */}
                            {alreadyAddedAssets.map(asset => {
                                const projectAsset = existingProjectAssets.find(pa => {
                                    const projectAssetMasterId = typeof pa.masterAssetId === 'string'
                                        ? pa.masterAssetId
                                        : pa.masterAssetId?._id || pa.masterAssetId?.id;
                                    return projectAssetMasterId === asset.masterAssetId;
                                });

                                return (
                                    <div
                                        key={asset.masterAssetId}
                                        className="bg-gray-50 rounded-lg border border-gray-300 p-4"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-3 flex-1">
                                                <div className="pt-0.5">
                                                    <input
                                                        type="checkbox"
                                                        checked={true}
                                                        disabled={true}
                                                        className="w-4 h-4 rounded text-gray-400 border-gray-400 cursor-not-allowed"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="text-sm font-semibold text-gray-700">{asset.name}</h4>
                                                    {asset.description && (
                                                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{asset.description}</p>
                                                    )}
                                                    <div className="flex items-center gap-1 mt-2">
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                                                            ✓ Already added to project
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {onRemoveAsset && projectAsset && (
                                                <button
                                                    onClick={() => onRemoveAsset(projectAsset)}
                                                    className="px-3 py-1.5 bg-white text-red-600 border border-red-300 rounded-md text-xs font-medium hover:bg-red-50 hover:border-red-400 transition-colors shadow-sm flex items-center gap-1 flex-shrink-0"
                                                    title="Remove from project"
                                                >
                                                    <Trash2 size={14} />
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>

                {onBulkAdd && (
                    <div className="p-4 bg-white border-t sticky bottom-0 z-10">
                        <button
                            onClick={handleBulkAddClick}
                            disabled={selectedAssetIds.size === 0}
                            className={`w-full py-2.5 rounded-lg font-medium shadow-sm transition-all flex items-center justify-center gap-2 ${selectedAssetIds.size === 0
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-[#339274] text-white hover:bg-[#29795f]'
                                }`}
                        >
                            <Plus size={18} />
                            Add {selectedAssetIds.size} Assets to Project
                        </button>
                    </div>
                )}
            </div>
        </RightDrawer>
    );
}
